import React, { useState, useEffect } from 'react';
import PinPadChallenge from '../../components/auth/PinPadChallenge';
import { toTitleCasePt } from '../../utils/formatters';

function maskCpf(cpf) {
  const d = String(cpf || '').replace(/\D/g, '');
  if (d.length !== 11) return null;
  return `***.***.${d.slice(6, 9)}-${d.slice(9)}`;
}

function getDisplayName(profile) {
  return (
    profile?.qualification?.physical?.socialName ||
    profile?.qualification?.physical?.name ||
    profile?.qualification?.minor?.name ||
    profile?.name ||
    profile?.fullName ||
    profile?.contact?.name ||
    null
  );
}

function isPendingProfile(profile) {
  return !getDisplayName(profile);
}

function getSecondaryInfo(profile) {
  const cpf = profile?.qualification?.physical?.cpf;
  if (cpf) {
    const masked = maskCpf(cpf);
    if (masked) return { label: 'CPF', value: masked };
  }
  const type = profile?.type;
  if (type) return { label: 'Natureza', value: type };
  if (isPendingProfile(profile)) return { label: 'Status', value: 'Aguardando preenchimento pelo cliente' };
  return null;
}

function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function ProfileSelector({ profiles, onSelect }) {
  // STATE SNAPSHOT ISOLATION (SSI): o objeto é congelado na RAM do React no
  // momento do clique. Mesmo que o Firestore atualize profileCandidates via
  // onSnapshot, frozenProfile NÃO muda → PinPadChallenge NÃO desmonta → sem flicker.
  const [frozenProfile, setFrozenProfile] = useState(null);

  // ERRADICAÇÃO DO GHOST LOCK: Higieniza artefatos de sessão zumbi no instante da montagem.
  // Garante que qualquer chave residual de uma sessão anterior não trave os cliques.
  useEffect(() => {
    try { sessionStorage.removeItem('mfaVerifiedClient'); } catch { void 0; }
    try { sessionStorage.removeItem('activeProfileId'); } catch { void 0; }
  }, []);

  if (frozenProfile) {
    return (
      <PinPadChallenge
        profile={frozenProfile}
        onSuccess={(perfil) => onSelect(perfil)}
        onCancel={() => { sessionStorage.removeItem('mfaVerifiedClient'); setFrozenProfile(null); }}
      />
    );
  }

  return (
    <div className="w-full space-y-4">

      {/* Header card — padrão "Área Segura do Cliente" */}
      <div className="rounded-2xl bg-[#20243B] px-6 py-5 shadow-md">
        <div className="flex items-center gap-2 mb-3">
          <i className="fas fa-shield-alt text-[#C5A059] text-[11px]" aria-hidden="true" />
          <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#C5A059]">
            Área Segura do Cliente
          </span>
        </div>
        <h1 className="text-xl font-extrabold text-white uppercase tracking-wide leading-tight">
          Quem está acessando?
        </h1>
        <p className="mt-2 text-xs text-slate-400 leading-relaxed max-w-md">
          Identificamos mais de um acesso principal vinculado a este e-mail
          (ex: e-mail familiar compartilhado). Selecione o seu perfil.
        </p>
      </div>

      {/* Profile cards */}
      <div className="space-y-2">
        {(profiles || []).map((profile, idx) => {
          const secondary = getSecondaryInfo(profile);
          const pending = isPendingProfile(profile);
          const displayName = getDisplayName(profile) || 'Cadastro Pendente';
          const uniqueDocId = profile?.id || profile?.uid || null;

          return (
            <button
              key={uniqueDocId || idx}
              type="button"
              onClick={() => {
                if (!uniqueDocId) return;
                console.log('[RDA Auth] Perfil selecionado:', getDisplayName(profile), '| ID:', uniqueDocId);
                try { sessionStorage.removeItem('mfaVerifiedClient'); } catch { void 0; }
                try { sessionStorage.removeItem('activeProfileId'); } catch { void 0; }
                setFrozenProfile(profile);
              }}
              className="group w-full flex flex-col justify-center px-6 py-5 rounded-2xl border border-gray-200 bg-white hover:border-[#C5A059] hover:shadow-md transition-all cursor-pointer text-left"
            >
              <div className="flex items-center gap-2">
                <p className="text-base font-extrabold text-[#20243B] tracking-wide w-full truncate">
                  {toTitleCasePt(displayName)}
                </p>
                {pending && (
                  <span className="shrink-0 text-[9px] font-extrabold uppercase tracking-widest bg-amber-200 text-amber-800 px-1.5 py-0.5 rounded-full">
                    NOVO
                  </span>
                )}
              </div>
              {secondary && (
                <p className="mt-1 text-xs text-gray-500 w-full truncate">
                  <span className="font-semibold text-gray-400">{secondary.label}:</span>{' '}
                  {secondary.value}
                </p>
              )}
            </button>
          );
        })}
      </div>

      {/* Footer note */}
      <p className="text-[11px] text-gray-400 text-center pt-1">
        Você poderá trocar de perfil a qualquer momento fazendo um novo login.
      </p>
    </div>
  );
}
